import { useEffect, useRef, useState } from "react";
import { toast } from "../../hooks/useToast";
import {C, withAlpha } from "../../constants/theme";

const REGEX_URL_IMAGEM = /^https?:\/\/\S+$/i;

// Entrada de fotos/vídeos (clique / arrastar / colar Ctrl+V / colar link) +
// grade de miniaturas com remoção. Reaproveitado em Produtos (galeria no
// Storage) e no Orçamento da Calculadora (array em base64) — quem chama
// decide o que fazer com os File[] recebidos em onAdicionar. Sem limite de
// tamanho — quem sobe é quem escolhe o que manda.
export function FotoUploader({
  fotos = [], onRemover, onAdicionar, onAbrirZoom, onReordenar,
  multiple = true, processando = false, accept = "image/*,video/*",
  compact = false, disabled = false,
}) {
  const [arrastando, setArrastando] = useState(false);
  const [linkAberto, setLinkAberto] = useState(false);
  const [link, setLink] = useState("");
  const [baixandoLink, setBaixandoLink] = useState(false);
  const inputRef = useRef();
  const dropRef = useRef();
  const tam = compact ? 56 : 70;

  // Arrastar uma miniatura pra reordenar — igual app (Fotos/tela inicial do
  // celular): ao passar por cima das outras, elas "escorregam" pro lado na
  // hora pra abrir espaço, não só troca de lugar no soltar. dragIdRef !=null
  // identifica "isso é reorder interno" pra não confundir com arrastar um
  // arquivo novo de fora pra cima da dropzone. A ordem só é persistida
  // (onReordenar) no final — durante o arraste é só reflow visual local.
  // ordemPreviewRef é a fonte da verdade (sempre atual, sem depender do React
  // re-renderizar a tempo entre um dragover e o drop seguinte); ordemPreview
  // (state) só existe pra desenhar o reflow na tela.
  const dragIdRef = useRef(null);
  const ordemPreviewRef = useRef(null);
  const [ordemPreview, setOrdemPreview] = useState(null); // array de ids, só enquanto arrasta
  const listaExibida = ordemPreview ? ordemPreview.map(id => fotos.find(f => f.id === id)).filter(Boolean) : fotos;

  const onThumbDragStart = (id) => (e) => {
    dragIdRef.current = id;
    const inicial = fotos.map(f => f.id);
    ordemPreviewRef.current = inicial;
    setOrdemPreview(inicial);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(id));
  };
  const onThumbDragOver = (id) => (e) => {
    if (dragIdRef.current === null) return;
    e.preventDefault(); e.stopPropagation();
    if (dragIdRef.current === id) return;
    const atual = ordemPreviewRef.current || fotos.map(f => f.id);
    const from = atual.indexOf(dragIdRef.current);
    const to = atual.indexOf(id);
    if (from === -1 || to === -1 || from === to) return;
    const nova = [...atual];
    nova.splice(from, 1);
    nova.splice(to, 0, dragIdRef.current);
    ordemPreviewRef.current = nova;
    setOrdemPreview(nova);
  };
  const finalizarDrag = () => {
    if (dragIdRef.current !== null && ordemPreviewRef.current) onReordenar?.(ordemPreviewRef.current);
    dragIdRef.current = null;
    ordemPreviewRef.current = null;
    setOrdemPreview(null);
  };
  const onThumbDrop = (e) => { if (dragIdRef.current === null) return; e.preventDefault(); e.stopPropagation(); finalizarDrag(); };

  // Respeita o `accept` de verdade (não só no seletor de arquivo nativo) —
  // importante pro Orçamento, que passa accept="image/*" e não deve aceitar
  // vídeo (fica em base64 no Firestore, um vídeo ali explodiria o documento).
  const tiposAceitos = accept.split(",").map(s => s.trim()).filter(Boolean);
  const tipoPermitido = (mime) => tiposAceitos.some(t => t.endsWith("/*") ? mime.startsWith(t.slice(0, -1)) : mime === t);

  const addFiles = (lista) => {
    const arr = [...lista].filter(f => tipoPermitido(f.type));
    if (arr.length > 0) onAdicionar(arr);
  };

  // Colar (Ctrl+V) — imagem de verdade (print, "copiar imagem") sempre
  // funciona, mesmo com outro campo focado. Link como texto só quando o
  // foco estiver na própria dropzone, pra não roubar paste de outros campos.
  useEffect(() => {
    if (disabled) return;
    const onPasteGlobal = (e) => {
      const items = [...(e.clipboardData?.items || [])];
      const imagens = items.filter(i => tipoPermitido(i.type));
      if (imagens.length > 0) {
        e.preventDefault();
        addFiles(imagens.map(i => i.getAsFile()).filter(Boolean));
        return;
      }
      const texto = e.clipboardData?.getData("text")?.trim();
      if (texto && REGEX_URL_IMAGEM.test(texto) && document.activeElement === dropRef.current) {
        e.preventDefault();
        importarPorUrl(texto);
      }
    };
    document.addEventListener("paste", onPasteGlobal);
    return () => document.removeEventListener("paste", onPasteGlobal);
  }, [disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const importarPorUrl = async (url) => {
    try { new URL(url); } catch { toast("Link inválido.", "warning"); return; }
    setBaixandoLink(true);
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      if (!tipoPermitido(blob.type)) throw new Error();
      const nome = url.split("/").pop()?.split("?")[0] || "imagem.jpg";
      addFiles([new File([blob], nome, { type: blob.type })]);
      setLink(""); setLinkAberto(false);
    } catch {
      toast("Não foi possível baixar essa imagem direto do link (o site pode bloquear acesso externo). Tente: botão direito → Salvar imagem → arraste o arquivo aqui, ou copie a imagem (Ctrl+C) e cole aqui (Ctrl+V).", "warning");
    }
    setBaixandoLink(false);
  };

  return (
    <div>
      {onReordenar && fotos.length > 1 && !disabled && (
        <div style={{ display:"flex", alignItems:"center", gap:5, color:C.muted, fontSize:10.5, marginBottom:6 }}>
          <span style={{ fontSize:12 }}>🔀</span> Arraste para mudar a ordem
        </div>
      )}
      <div
        ref={dropRef}
        tabIndex={0}
        onDragOver={e => { if (dragIdRef.current !== null) return; e.preventDefault(); if (!disabled) setArrastando(true); }}
        onDragLeave={() => setArrastando(false)}
        onDrop={e => {
          if (dragIdRef.current !== null) return;
          e.preventDefault(); setArrastando(false);
          if (!disabled) addFiles(e.dataTransfer.files);
        }}
        style={{ display:"flex", flexWrap:"wrap", gap:8, padding:8, borderRadius:10, outline:"none",
          border:"1.5px dashed "+(arrastando ? C.accent2 : "transparent"),
          background:arrastando ? withAlpha(C.accent2, "11") : "transparent", transition:"all 0.12s" }}>
        {listaExibida.map((foto, i) => (
          <div key={foto.id}
            draggable={!disabled && !!onReordenar}
            onDragStart={onThumbDragStart(foto.id)}
            onDragOver={onThumbDragOver(foto.id)}
            onDrop={onThumbDrop}
            onDragEnd={finalizarDrag}
            title={onReordenar ? "Arraste pra reordenar — a 1ª é a foto principal" : undefined}
            style={{ position:"relative", width:tam, height:tam, flexShrink:0,
              opacity: dragIdRef.current===foto.id ? 0.4 : 1,
              transition: "transform 0.15s, opacity 0.15s",
              cursor: onReordenar && !disabled ? "grab" : "default" }}>
            {i === 0 && (
              <span title="Foto principal" style={{ position:"absolute", top:-6, left:-6, fontSize:11, background:C.accent2, color:"#06281f", borderRadius:"50%", width:16, height:16, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:900, zIndex:1 }}>★</span>
            )}
            {foto.tipo === "video" ? (
              <video src={foto.url} muted draggable={false} onClick={()=>onAbrirZoom?.(i)}
                style={{ width:tam, height:tam, objectFit:"cover", borderRadius:8, border:"1px solid "+C.border, cursor:onAbrirZoom?"zoom-in":"default", pointerEvents: ordemPreview?"none":"auto" }} />
            ) : (
              <img src={foto.url} alt={`foto ${i+1}`} draggable={false} onClick={()=>onAbrirZoom?.(i)}
                style={{ width:tam, height:tam, objectFit:"cover", borderRadius:8, border:"1px solid "+C.border, cursor:onAbrirZoom?"zoom-in":"default", pointerEvents: ordemPreview?"none":"auto" }} />
            )}
            {foto.tipo === "video" && (
              <span style={{ position:"absolute", bottom:3, left:3, fontSize:12, pointerEvents:"none", textShadow:"0 1px 3px rgba(0,0,0,0.8)" }}>▶️</span>
            )}
            {!disabled && (
              <button onClick={()=>onRemover(foto.id)} title="Remover foto"
                style={{ position:"absolute", top:-6, right:-6, width:20, height:20, borderRadius:"50%", background:C.danger, border:"2px solid "+C.panel, color:"#fff", fontSize:11, fontWeight:900, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", padding:0, lineHeight:1 }}>✕</button>
            )}
          </div>
        ))}
        <input ref={inputRef} type="file" accept={accept} multiple={multiple} style={{ display:"none" }}
          onChange={e => { addFiles(e.target.files); e.target.value=""; }} />
        <button onClick={()=>inputRef.current.click()} disabled={disabled || processando}
          title="Clique, arraste um arquivo ou cole (Ctrl+V) uma imagem/vídeo aqui"
          style={{ width:tam, height:tam, borderRadius:8, border:"1.5px dashed "+C.border, background:"none", color:C.muted, cursor:disabled?"default":"pointer", fontSize:20, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          {processando ? "⏳" : "➕"}
        </button>
      </div>

      {!disabled && (
        <div style={{ color:C.muted, fontSize:10.5, marginTop:5 }}>📎 Arraste ou cole foto, gif ou vídeo (Ctrl+V)</div>
      )}

      {!disabled && (
        <div style={{ marginTop:6 }}>
          {!linkAberto ? (
            <button onClick={()=>setLinkAberto(true)} style={{ background:"none", border:"none", color:C.muted, fontSize:11, cursor:"pointer", padding:"2px 0", textDecoration:"underline" }}>
              🔗 Colar link de imagem/vídeo
            </button>
          ) : (
            <div style={{ display:"flex", gap:6, marginTop:2 }}>
              <input value={link} onChange={e=>setLink(e.target.value)} placeholder="https://..."
                onKeyDown={e=>{ if (e.key==="Enter" && link.trim()) importarPorUrl(link.trim()); }}
                style={{ flex:1, minWidth:0, background:C.card, border:"1px solid "+C.border, borderRadius:6, color:C.text, padding:"5px 8px", fontSize:12, outline:"none" }} />
              <button onClick={()=>link.trim() && importarPorUrl(link.trim())} disabled={baixandoLink}
                style={{ background:withAlpha(C.accent2, "22"), border:"1px solid "+withAlpha(C.accent2, "66"), color:C.accent2, borderRadius:6, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer" }}>
                {baixandoLink ? "⏳" : "Baixar"}
              </button>
              <button onClick={()=>{ setLinkAberto(false); setLink(""); }} style={{ background:"none", border:"none", color:C.muted, cursor:"pointer", fontSize:14, padding:"0 4px" }}>✕</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
